import { SpanKind } from "@opentelemetry/api";

import { DEFAULT_FLUSH_INTERVAL, ROOT_SPAN_NAME, WEB_TRACER_VERSION } from "../constants";
import { DocumentLoadInstrumentation } from "../instrumentations/document-load";
import CONV from "../semantic-conventions";
import { ProviderType, RootSpan, ViewConfigType } from "../types";
import { getFoldedPath, onNavigation } from "../utils";
import { error, log } from "../utils/logger";
import { ErrorCollector } from "./error";
import { WebVitals } from "./web-vitals";

export class ViewRootSpanManager {
  private provider: ProviderType;
  private tracer: any;
  private config: ViewConfigType;
  private flushTimerId: ReturnType<typeof setInterval> | null = null;
  private currentRootSpan?: RootSpan;
  private webVitals: WebVitals;
  private errorCollector: ErrorCollector;
  private documentLoad?: DocumentLoadInstrumentation;
  private _visibilityChangeListener?: () => void;
  private _pageHideListener?: () => void;

  constructor(provider: ProviderType, config: ViewConfigType) {
    this.provider = provider;
    this.tracer = provider.getTracer("web-tracer", WEB_TRACER_VERSION);
    this.config = config;
    this.webVitals = new WebVitals();
    this.errorCollector = new ErrorCollector();
    this.initializeInstruments();
  }

  public init() {
    log("[L9RUM View] Initializing root span management");
    this._startRootSpan();
    this._initEvents();
  }

  private initializeInstruments() {
    const { trackDocumentLoad, ignoreNetworkEvents, ignorePerformancePaintEvents } = this.config;

    if (trackDocumentLoad ?? false) {
      this.documentLoad = new DocumentLoadInstrumentation(this.tracer, {
        ignoreNetworkEvents,
        ignorePerformancePaintEvents,
      });
    }
  }

  private _initEvents = () => {
    this._initAutoFlushInterval();
    this._initNavigationEvents();
    this._initDocumentEvents();
  };

  private _initDocumentEvents = () => {
    if (typeof document !== "undefined") {
      this._visibilityChangeListener = async () => {
        if (document.visibilityState === "hidden") {
          log("[L9RUM View] Document hidden, ending root span and flushing");
          await this._endRootSpan();
          try {
            await this.provider.forceFlush();
            log("[L9RUM View] Tracer provider flushed (visibility change)");
          } catch (err) {
            error("[L9RUM View] Error flushing tracer provider (visibility change)", err);
          }
        }
      };
      this._pageHideListener = async () => {
        log("[L9RUM View] Page hidden, ending root span and flushing");
        await this._endRootSpan();
        try {
          await this.provider.forceFlush();
          log("[L9RUM View] Tracer provider flushed (page hide)");
        } catch (err) {
          error("[L9RUM View] Error flushing tracer provider (page hide)", err);
        }
      };
      document.addEventListener("visibilitychange", this._visibilityChangeListener);

      // use 'pagehide' event as a fallback for Safari; see https://bugs.webkit.org/show_bug.cgi?id=116769
      document.addEventListener("pagehide", this._pageHideListener);
    }
  };

  private _initNavigationEvents = () => {
    // On navigation, end the current root span and start a new one
    onNavigation(async () => {
      log("[L9RUM View] Navigation detected, ending previous root span and starting new root span");

      await this._endRootSpan();
      this._startRootSpan();
    });
  };

  private _initAutoFlushInterval = () => {
    const { flushIntervalMs = DEFAULT_FLUSH_INTERVAL } = this.config;
    this._clearFlushInterval();
    // Flush the root span every flushIntervalMs
    this.flushTimerId = setInterval(async () => {
      log("[L9RUM View] Flush Interval, ending root span and flushing");
      await this._endRootSpan();
      try {
        await this.provider.forceFlush();
        log("[L9RUM View] Tracer provider flushed (interval)");
      } catch (err) {
        error("[L9RUM View] Error flushing tracer provider (interval)", err);
      }
    }, flushIntervalMs);
  };

  private _startRootSpan() {
    // Unsubscribe from web vitals and error collector
    this.webVitals.unsubscribe();
    this.errorCollector.unsubscribe();

    const rootSpanAttributes = this._getRootSpanAttributes();
    this.currentRootSpan = this.tracer.startSpan(ROOT_SPAN_NAME, {
      kind: SpanKind.INTERNAL,
      attributes: rootSpanAttributes,
    });
    log("[L9RUM View] Started root span", rootSpanAttributes);

    // Start adding web vitals and error events to the root span
    if (this.currentRootSpan) {
      this.webVitals.subscribe(this.currentRootSpan);
      this.errorCollector.subscribe(this.currentRootSpan);
      this.documentLoad?.subscribe(this.currentRootSpan);
    }
  }

  private async _endRootSpan() {
    if (this.currentRootSpan) {
      log("[L9RUM View] Ending root span");
      this.currentRootSpan.end();
      this.webVitals.unsubscribe();
      this.errorCollector.unsubscribe();
      this.currentRootSpan = undefined;
      this._clearFlushInterval();
    }
  }

  private _clearFlushInterval() {
    if (this.flushTimerId) {
      clearInterval(this.flushTimerId);
      this.flushTimerId = null;
    }
  }

  private _getRootSpanAttributes() {
    const location = window.location;

    return {
      origin: location.origin,
      path: getFoldedPath(location.pathname),
      [CONV.PAGE_URL]: location.href,
      [CONV.PAGE_HASH]: location.hash,
      [CONV.PAGE_ROUTE]: location.pathname,
      [CONV.PAGE_HOSTNAME]: location.hostname,
      [CONV.PAGE_SEARCH]: location.search,
      [CONV.URL_PATH]: location.pathname,
    };
  }

  protected onShutdown(): void {
    if (typeof document !== "undefined") {
      if (this._visibilityChangeListener) {
        document.removeEventListener("visibilitychange", this._visibilityChangeListener);
      }
      if (this._pageHideListener) {
        document.removeEventListener("pagehide", this._pageHideListener);
      }
    }
  }
}
